import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../../../../API';
import { useAuth } from '../../../../utils/Auth';
import { useOrganization } from '../../../../utils/OrganizationContext';
import RoleSelector from '../RoleSelector';
import { ROLES } from '../../../../utils/Auth/roleUtils';
import styles from './Invite.module.css';

const InviteMember = ({ onSuccess }) => {
  const navigate = useNavigate();
  const { activeOrgId } = useAuth();
  const { organizations, orgMembers } = useOrganization();
  const [inviteEmail, setInviteEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState(ROLES.EDITOR);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const currentOrg = organizations?.find(org => org.id === activeOrgId);

  // Check if organization is at or over team capacity
  const currentMemberCount = orgMembers?.length || 0;
  const teamSizeLimit = currentOrg?.plan?.team_size || 0;
  const isAtCapacity = currentMemberCount >= teamSizeLimit;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!inviteEmail.trim()) {
      setError('Invite email is required');
      return;
    }

    if (!activeOrgId) {
      setError('Organization ID is missing');
      return;
    }

    setStatus('loading');

    try {
      await API.sendOrgMemberInvite({
        inviteEmail: inviteEmail.trim(),
        org_id: activeOrgId,
        role: selectedRole
      });

      setInviteEmail('');
      setSelectedRole(ROLES.EDITOR);
      setStatus('success');
      onSuccess?.();

      // Clear success message after 2 seconds and redirect
      setTimeout(() => {
        navigate('/organizations/members');
      }, 2000);
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Failed to send invite';
      setError(message);
      setStatus('error');
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerTop}>
            <button className={styles.backButton} onClick={() => navigate('/organizations/members')}>
              <i className='fas fa-chevron-left'></i>
            </button>
            <h1 className={styles.title}>Invite Team Member</h1>
          </div>
            {currentOrg?.name && (
              <div className={styles.headerBottom}>
                <p className={styles.orgName}>Organization: <strong>{currentOrg.name}</strong></p>
              </div>
            )}
        </div>

        <form className={styles.form} onSubmit={handleSubmit}>
          <p className={styles.description}>
            Send an invitation to join your organization. They'll receive an email with a signup link.
          </p>

          {isAtCapacity && (
            <div className={styles.capacityWarning}>
              <i className="fas fa-exclamation-triangle"></i>
              <strong>Team capacity reached ({currentMemberCount}/{teamSizeLimit}).</strong>
              <p>Your organization has reached its maximum team size. Please upgrade your plan to invite more members.</p>
            </div>
          )}

          <div className={styles.formGroup}>
            <label htmlFor='inviteEmail' className={styles.label}>
              Email Address
            </label>
            <input
              id='inviteEmail'
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="person@example.com"
              className={styles.input}
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor='role' className={styles.label}>
              Role
            </label>
            <RoleSelector
              unrestricted
              value={selectedRole}
              onRoleChange={setSelectedRole}
              disabled={status === 'loading' || isAtCapacity}
            />
          </div>

          <div className={styles.actions}>
            <button
              type="submit"
              disabled={status === 'loading' || isAtCapacity}
              className={`${styles.submitButton} ${isAtCapacity ? styles.submitButtonDisabled : ''}`}
            >
              {status === 'loading' ? 'Sending…' : isAtCapacity ? 'Capacity Reached' : 'Send Invite'}
            </button>
          </div>

          {status === 'success' && <p className={styles.success}>Invite sent! Redirecting…</p>}
          {error && <p className={styles.error}>{error}</p>}
        </form>
      </div>
    </div>
  );
};

export default InviteMember;
